import { 
    Badge,
    Box, 
    Typography, 
    Button,
    IconButton,
    Tooltip,
    LinearProgress,
    CircularProgress 
} from '@mui/material';
import * as Icons from '@mui/icons-material';
import { useCurrentHashStore } from '../../stores/hashs/useCurrentHashStore';
import { useAlertN2Store } from '../../stores/hashs/useAlertN2Store';
import { n2AlertConfig } from '../../utils/hashs/hashConfig';
import { useLoadingStore, selectIsMainLoading } from '../../stores/useLoadingStore';
import ProfilesBtn from './ProfilesBtn';

export default function AlertN2Button({ isCompactMode }) {

    const IconComponent = Icons[n2AlertConfig.icon] || Icons.Warning;

    const setCurrentHash = useCurrentHashStore(state => state.setCurrentHash);

    const currentHash = useCurrentHashStore(state => state.currentHash);

    const loading = useLoadingStore(state => state.loading);

    const loadProgress = useLoadingStore(state => state.loadProgress);

    const isMainLoading = useLoadingStore(selectIsMainLoading);

    const isActive = (currentHash === n2AlertConfig.hash);

    const data = useAlertN2Store(state => state.data);
    const total = useAlertN2Store(state => state.total);
    const totalDay = useAlertN2Store(state => state.totalDay);
    const percentTotal = useAlertN2Store(state => state.percentTotal); 

    if (isCompactMode) {

        return (
            <Tooltip
                placement="right"
                arrow
                title={
                    <Box>
                        <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            mb={0.5}
                        >
                            {n2AlertConfig.title}
                        </Typography>

                        <Typography variant="body2">
                            Alertas: <strong>{total}</strong>
                        </Typography>

                        <Typography variant="body2">
                            Total: <strong>{totalDay}</strong>
                        </Typography>

                        <Typography variant="body2">
                            Percentual: <strong>{percentTotal}%</strong>
                        </Typography>
                    </Box>
                }
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {isMainLoading && (
                        <CircularProgress
                            variant="determinate"
                            value={loadProgress ?? 0}
                            color={n2AlertConfig.color}
                            size={48}
                            thickness={3.5}
                            sx={{
                                position: 'absolute'
                            }}
                        />
                    )}

                    <Badge
                        badgeContent={total ?? 0}
                        color={n2AlertConfig.color}
                        max={99}
                        overlap="circular"
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        sx={{
                            overflow: 'visible',
                            zIndex: 2,

                            '& .MuiBadge-badge': {
                                backgroundColor: 'error.main',
                                color: 'error.contrastText',

                                fontSize: 10,
                                fontWeight: 700,
                                lineHeight: 1,

                                minWidth: 18,
                                height: 18,
                                padding: '0 4px',

                                borderRadius: 9,

                                border: '2px solid',
                                borderColor: 'background.paper',

                                boxShadow: 1,

                                top: 0,
                                right: 0,

                                transform:
                                    'translate(34%, -34%)',

                                transformOrigin:
                                    '100% 0%',

                                zIndex: 10
                            }
                        }}
                    >
                        <IconButton
                            color={n2AlertConfig.color}
                            disabled={loading}
                            onClick={() =>
                                setCurrentHash(
                                    n2AlertConfig.hash
                                )
                            }
                            sx={{
                                width: 40,
                                height: 40,

                                border: 2,
                                borderStyle: 'solid',
                                borderColor: isActive
                                    ? 'error.main'
                                    : `${n2AlertConfig.color}.main`,

                                bgcolor: isActive
                                    ? `${n2AlertConfig.color}.main`
                                    : 'background.paper',

                                color: isActive
                                    ? `${n2AlertConfig.color}.contrastText`
                                    : `${n2AlertConfig.color}.main`,

                                boxShadow: 2,

                                transform: isActive
                                    ? 'scale(1.08)'
                                    : 'scale(1)',

                                transition: 'all 0.2s ease-in-out',

                                '&:hover': {
                                    bgcolor:
                                        `${n2AlertConfig.color}.main`,
                                    color:
                                        `${n2AlertConfig.color}.contrastText`,
                                    transform: 'scale(1.08)'
                                }
                            }}
                        >
                            <IconComponent
                                sx={{
                                    fontSize: 27
                                }}
                            />
                        </IconButton>
                    </Badge>
                </Box>
            </Tooltip>
        );

    }

    return (
        <Button
            sx={(theme) => ({
                borderRadius: 1,
                paddingX: 1.5,
                display: 'flex',
                flexDirection: 'column',
                flexBasis: {
                    xs: '100%',
                    md: n2AlertConfig.width,
                },
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                textAlign: 'left',
                position: 'relative',

                borderWidth:
                    isActive ? 3 : 0,
                borderStyle:
                    isActive ? 'solid' : 'none',
                borderColor:
                    isActive
                        ? theme.palette.error.main
                        : undefined,

                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                transition: 'transform 0.2s ease-in-out',
                transformOrigin: 'center center'
            })}
            variant={n2AlertConfig.variant}
            color={n2AlertConfig.color}
            loading={loading}
            onClick={() => setCurrentHash(n2AlertConfig.hash)}
        >
            {/* PROGRESSO */}
            {isMainLoading && (
                <LinearProgress
                    variant="determinate"
                    value={loadProgress ?? 0}
                    color={n2AlertConfig.color}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        width: '100%',
                        height: 4
                    }}
                />
            )}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    gap: .5,
                    alignItems: 'center',
                    marginBottom: 1
                }}
            >
                <IconComponent sx={{ fontSize: 24 }} />
                <Typography variant="body2">
                    <strong>{n2AlertConfig.title}</strong>
                </Typography>
            </Box>

            <ProfilesBtn
                data={data}
                total={total}
            />
            
            {!isMainLoading && (
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingTop: 1,
                        borderTop: 2,
                        marginTop: 1,
                        gap: 0
                    }}
                >
                    <Box
                        sx={{
                            px: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0
                        }}
                    >
                        <Typography variant="h4" fontWeight="300">{total}</Typography>
                        <Typography sx={{marginTop: -1}} variant="subtitle2" fontWeight="300">Total: {totalDay}</Typography>
                    </Box> 
                    <Typography variant="h6">({percentTotal}%)</Typography>               
                </Box>
            )}
        </Button>
    );

}